/**
 * Lift2Fit — the live Grid's cell and prescription reader.
 *
 * The signed-in Grid writes through the API instead of localStorage, so it
 * cannot reuse `useWorkoutLog`/`WorkoutGrid` (whose public contract is the
 * offline demo). This module keeps the landing page's visual language — dashed
 * empty slot, `weight × reps @ RPE`, colour + glyph + word — while every commit
 * is a POST/PATCH/DELETE. `SessionTotals` and `StatusTag` are shared verbatim.
 */
import { useEffect, useRef, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import type { SessionSet, SetMeasure, SetStatus } from '@/lib/api';
import type { PrescriptionLine } from '@/lib/prescription';
import { cn } from '@/lib/format';
import { displayToKg, kgToDisplay } from '@/lib/units';
import { CheckIcon, PlusIcon } from '@/lib/icons';
import { StatusTag } from '@/components/StatusTag';

/* ---------------------------------------------------------------------------
   Prescription reader
   ------------------------------------------------------------------------ */

export interface ExercisePrescription {
  sets: number;
  repMin: number | null;
  repMax: number | null;
  targetRpe: number | null;
  targetWeightKg: number | null;
  restSeconds: number;
  measure: SetMeasure;
  perSide: boolean;
  supersetGroup: number | null;
  groupType: 'superset' | 'drop_set' | null;
  tempo: string | null;
  notes: string | null;
  isBodyweight: boolean;
}

const MEASURES: ReadonlyArray<SetMeasure> = ['reps', 'distance_m', 'time_s', 'amrap'];

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Tolerate whatever the snapshot carries: an older plan has fewer keys. */
export function parsePrescription(raw: Record<string, unknown>): ExercisePrescription {
  const measure = MEASURES.includes(raw.measure as SetMeasure) ? (raw.measure as SetMeasure) : 'reps';
  const supersetGroup = asNumber(raw.supersetGroup);
  const rawType = raw.groupType;
  return {
    sets: Math.max(0, Math.round(asNumber(raw.sets) ?? 0)),
    repMin: asNumber(raw.repMin),
    repMax: asNumber(raw.repMax),
    targetRpe: asNumber(raw.targetRpe),
    targetWeightKg: asNumber(raw.targetWeightKg),
    restSeconds: Math.max(0, Math.round(asNumber(raw.restSeconds) ?? 0)),
    measure,
    perSide: raw.perSide === true,
    supersetGroup,
    // A legacy snapshot has a group number but no type: it was a superset.
    groupType:
      rawType === 'drop_set' ? 'drop_set' : rawType === 'superset' || supersetGroup !== null ? 'superset' : null,
    tempo: typeof raw.tempo === 'string' ? raw.tempo : null,
    notes: typeof raw.notes === 'string' ? raw.notes : null,
    isBodyweight: raw.isBodyweight === true,
  };
}

/**
 * Which logged sets read as a drop set: consecutive working sets on the same
 * movement where the load falls by at least 15 % and the reps hold or rise.
 * Read from the log itself, never from the plan.
 */
export function dropSetSetIndexes(sets: SessionSet[]): Set<number> {
  const working = sets
    .filter((set) => !set.isWarmup && set.status !== 'pending' && set.measure === 'reps' && set.weightKg > 0)
    .sort((a, b) => a.setIndex - b.setIndex);
  const flagged = new Set<number>();
  for (let index = 1; index < working.length; index += 1) {
    const previous = working[index - 1];
    const current = working[index];
    if (!previous || !current) continue;
    if (current.weightKg <= previous.weightKg * 0.85 && current.reps >= previous.reps) {
      flagged.add(previous.setIndex);
      flagged.add(current.setIndex);
    }
  }
  return flagged;
}

/**
 * The parsed prescription in the shared formatting vocabulary, so the Grid, the
 * Programme editor and the session runtime print the same line for a movement.
 */
export function prescriptionLine(prescription: ExercisePrescription): PrescriptionLine {
  return {
    supersetGroup: prescription.supersetGroup,
    prescribedSets: prescription.sets,
    repMin: prescription.repMin,
    repMax: prescription.repMax,
    measure: prescription.measure,
    perSide: prescription.perSide,
    isBodyweight: prescription.isBodyweight,
    tempo: prescription.tempo,
    targetRpe: prescription.targetRpe,
    percent1rm: null,
    targetWeightKg: prescription.targetWeightKg,
    targetDistanceM: prescription.measure === 'distance_m' ? (prescription.repMax ?? prescription.repMin) : null,
    targetDurationS: prescription.measure === 'time_s' ? (prescription.repMax ?? prescription.repMin) : null,
    restSeconds: prescription.restSeconds,
  };
}

export interface SetDraft {
  weightKg: number;
  reps: number;
  rpe: number | null;
  distanceM: number | null;
  durationS: number | null;
  painScore: number | null;
}

/** The prescribed set, as the one-tap value for an empty slot. */
export function prescribedDraft(prescription: ExercisePrescription): SetDraft {
  return {
    weightKg: prescription.targetWeightKg ?? 0,
    reps: prescription.repMax ?? prescription.repMin ?? 8,
    rpe: null,
    distanceM: prescription.measure === 'distance_m' ? (prescription.repMax ?? prescription.repMin ?? 0) : null,
    durationS: prescription.measure === 'time_s' ? (prescription.repMax ?? prescription.repMin ?? 0) : null,
    painScore: null,
  };
}

export function draftOfSet(set: SessionSet): SetDraft {
  return {
    weightKg: set.weightKg,
    reps: set.reps,
    rpe: set.rpe,
    distanceM: set.distanceM,
    durationS: set.durationS,
    painScore: set.painScore,
  };
}

/** The compact "65 × 5" reference printed in an empty slot. */
export function targetLabel(prescription: ExercisePrescription, unit: UnitSystem): string {
  if (prescription.measure === 'distance_m') {
    return `${prescription.repMax ?? prescription.repMin ?? 0} m`;
  }
  if (prescription.measure === 'time_s') {
    return `${prescription.repMax ?? prescription.repMin ?? 0} s`;
  }
  const weight = prescription.targetWeightKg;
  const reps = prescription.repMax ?? prescription.repMin ?? 8;
  const load = weight === null || weight <= 0 ? 'BW' : String(kgToDisplay(weight, unit));
  return `${load} × ${reps}`;
}

/* ---------------------------------------------------------------------------
   The cell
   ------------------------------------------------------------------------ */

const RPE_OPTIONS = ['', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];

export interface GridSetCellProps {
  exerciseName: string;
  /** the session exercise id — makes each cell's test id unique */
  rowId: string;
  setNumber: number;
  set: SessionSet | null;
  prescription: ExercisePrescription;
  unit: UnitSystem;
  /** `rowIndex-column`, the identity the roving keyboard focus uses */
  cellId: string;
  editing: boolean;
  isFocused: boolean;
  busy: boolean;
  canRemoveSlot: boolean;
  /** logged-set detection: this set is part of a drop set */
  isDropSet: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onLog: (draft: SetDraft) => void;
  onPatch: (draft: SetDraft, status?: Exclude<SetStatus, 'pr'>) => void;
  onDelete: () => void;
  onToggleDone: () => void;
  onRemoveSlot: () => void;
  onNavigate: (dir: 'left' | 'right' | 'up' | 'down') => void;
}

/**
 * One set. An empty slot is a one-tap log of the prescription; a logged set
 * edits in place, toggles done, or is deleted. Enter commits, `d` marks done,
 * Delete removes, Escape cancels, arrows move — the same contract as the
 * landing page's Grid.
 */
export function GridSetCell(props: GridSetCellProps) {
  const {
    exerciseName,
    rowId,
    setNumber,
    set,
    prescription,
    unit,
    cellId,
    editing,
    isFocused,
    busy,
    canRemoveSlot,
    isDropSet,
    onStartEdit,
    onStopEdit,
    onLog,
    onPatch,
    onDelete,
    onToggleDone,
    onRemoveSlot,
    onNavigate,
  } = props;

  const cellRef = useRef<HTMLDivElement>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const original = useRef<SetDraft | null>(null);
  const [draftWeight, setDraftWeight] = useState('');
  const [draftReps, setDraftReps] = useState('');
  const [draftRpe, setDraftRpe] = useState('');
  const [draftDistance, setDraftDistance] = useState('');
  const [draftDuration, setDraftDuration] = useState('');

  const isDistance = prescription.measure === 'distance_m';
  const isTime = prescription.measure === 'time_s';

  useEffect(() => {
    if (!editing) return;
    const base = set ? draftOfSet(set) : prescribedDraft(prescription);
    original.current = base;
    setDraftWeight(String(kgToDisplay(base.weightKg, unit)));
    setDraftReps(String(base.reps));
    setDraftRpe(base.rpe === null ? '' : String(base.rpe));
    setDraftDistance(String(base.distanceM ?? 0));
    setDraftDuration(String(base.durationS ?? 0));
    const id = window.setTimeout(() => {
      weightRef.current?.focus();
      weightRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
    // the draft is seeded only when the editor opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, unit]);

  const num = (value: string): number => Number.parseFloat(value) || 0;

  const readDraft = (): SetDraft => ({
    weightKg: displayToKg(num(draftWeight), unit),
    reps: Math.max(0, Math.round(num(draftReps))),
    rpe: draftRpe === '' ? null : Number.parseFloat(draftRpe),
    // Only send the measure's own field: the API rejects a distance of 0 on a
    // reps set (and vice versa) as a 422.
    distanceM: isDistance ? Math.round(num(draftDistance) * 10) / 10 : null,
    durationS: isTime ? Math.round(num(draftDuration)) : null,
    painScore: set?.painScore ?? null,
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (editing) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (set) onPatch(readDraft());
        else onLog(readDraft());
        onStopEdit();
        window.requestAnimationFrame(() => cellRef.current?.focus());
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onStopEdit();
        window.requestAnimationFrame(() => cellRef.current?.focus());
      }
      return;
    }
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        onStartEdit();
        break;
      case 'd':
      case 'D':
        event.preventDefault();
        if (set) onToggleDone();
        else onLog(prescribedDraft(prescription));
        break;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        if (set) onDelete();
        else if (canRemoveSlot) onRemoveSlot();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        onNavigate('left');
        break;
      case 'ArrowRight':
        event.preventDefault();
        onNavigate('right');
        break;
      case 'ArrowUp':
        event.preventDefault();
        onNavigate('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        onNavigate('down');
        break;
      default:
        break;
    }
  };

  const displayWeight = set ? kgToDisplay(set.weightKg, unit) : 0;
  const aria = set
    ? `${exerciseName}, set ${setNumber}, ${displayWeight} ${unit === 'imperial' ? 'lb' : 'kg'}, ${set.reps} reps${
        set.rpe === null ? '' : `, RPE ${set.rpe}`
      }, ${set.status}`
    : `${exerciseName}, set ${setNumber}, not logged. Press Enter to edit or D to log the prescription.`;

  const inputClass =
    'l2f-tabular rounded-none border border-hairline bg-surface px-1.5 py-1 font-mono text-[13px] font-bold text-ink';

  return (
    <div
      ref={cellRef}
      data-cell={cellId}
      role="gridcell"
      tabIndex={isFocused ? 0 : -1}
      aria-label={aria}
      data-testid={`${set ? 'setcell' : 'slot'}-${rowId}-${setNumber}`}
      onClick={() => {
        if (!editing && set) onStartEdit();
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative flex h-full min-h-[68px] flex-col justify-between gap-2 p-2.5 outline-none transition-colors duration-100',
        set && !editing && 'cursor-pointer hover:bg-page-subtle focus-visible:bg-page-subtle',
        editing && 'bg-page-subtle ring-2 ring-accent',
        set?.status === 'pr' && 'bg-accent/[0.07]',
        set?.isWarmup && 'bg-page-subtle/60',
      )}
    >
      {editing ? (
        <div className="flex flex-col gap-2" onClick={(event) => event.stopPropagation()}>
          {isDistance ? (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${cellId}-distance`}>
                Distance in metres
              </label>
              <input
                ref={weightRef}
                id={`${cellId}-distance`}
                type="number"
                inputMode="numeric"
                value={draftDistance}
                onChange={(event) => setDraftDistance(event.target.value)}
                className={cn(inputClass, 'w-20')}
              />
              <span className="text-[11px] text-ink-muted">m</span>
            </div>
          ) : isTime ? (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${cellId}-duration`}>
                Duration in seconds
              </label>
              <input
                ref={weightRef}
                id={`${cellId}-duration`}
                type="number"
                inputMode="numeric"
                value={draftDuration}
                onChange={(event) => setDraftDuration(event.target.value)}
                className={cn(inputClass, 'w-20')}
              />
              <span className="text-[11px] text-ink-muted">sec</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <label className="sr-only" htmlFor={`${cellId}-weight`}>
                Weight in {unit === 'imperial' ? 'pounds' : 'kilograms'}
              </label>
              <input
                ref={weightRef}
                id={`${cellId}-weight`}
                type="number"
                inputMode="decimal"
                step={unit === 'imperial' ? 5 : 2.5}
                value={draftWeight}
                onChange={(event) => setDraftWeight(event.target.value)}
                className={cn(inputClass, 'w-16')}
              />
              <span className="text-[11px] text-ink-muted">×</span>
              <label className="sr-only" htmlFor={`${cellId}-reps`}>
                Reps
              </label>
              <input
                id={`${cellId}-reps`}
                type="number"
                inputMode="numeric"
                value={draftReps}
                onChange={(event) => setDraftReps(event.target.value)}
                className={cn(inputClass, 'w-12')}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <label className="sr-only" htmlFor={`${cellId}-rpe`}>
              RPE
            </label>
            <select
              id={`${cellId}-rpe`}
              value={draftRpe}
              onChange={(event) => setDraftRpe(event.target.value)}
              className="rounded-none border border-hairline bg-surface px-1 py-1 font-mono text-[11px] text-ink"
            >
              {RPE_OPTIONS.map((value) => (
                <option key={value || 'none'} value={value}>
                  {value === '' ? 'RPE —' : `RPE ${value}`}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy}
              data-testid="cell-save"
              onClick={() => {
                if (set) onPatch(readDraft());
                else onLog(readDraft());
                onStopEdit();
              }}
              className="inline-flex h-7 items-center gap-1 bg-accent px-2 text-[10px] font-bold uppercase tracking-[0.06em] text-on-accent disabled:opacity-40"
            >
              <CheckIcon className="text-[11px]" /> Save
            </button>
            <button
              type="button"
              onClick={onStopEdit}
              className="text-[10px] font-bold uppercase tracking-[0.06em] text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : set ? (
        <>
          <div className="flex flex-col items-start gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onStartEdit();
              }}
              className="l2f-tabular text-left font-mono text-[13px] font-bold leading-none text-ink"
            >
              {set.measure === 'distance_m' ? (
                <>
                  {set.distanceM ?? 0}
                  <span className="ml-0.5 text-[10px] font-semibold text-ink-muted">m</span>
                </>
              ) : set.measure === 'time_s' ? (
                <>{set.durationS ?? 0}s</>
              ) : (
                <>
                  {set.weightKg > 0 ? displayWeight : 'BW'}
                  <span className="mx-1 text-ink-muted">×</span>
                  {set.reps}
                  {set.perSide ? <span className="text-[10px] font-semibold text-ink-muted">/side</span> : null}
                </>
              )}
              {set.rpe !== null ? (
                <span className="ml-1.5 text-[10px] font-semibold text-ink-muted">@{set.rpe}</span>
              ) : null}
            </button>
            {isDropSet ? (
              <span
                data-testid={`dropset-${rowId}-${setNumber}`}
                className="inline-flex items-center gap-1 rounded-full bg-accent px-1.5 py-[1px] text-[9px] font-black uppercase tracking-[0.06em] text-on-accent"
              >
                <span aria-hidden="true">↓</span> Drop set
              </span>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleDone();
              }}
              disabled={busy}
              aria-pressed={set.status !== 'pending'}
              aria-label={`${set.status === 'pending' ? 'Mark' : 'Reopen'} set ${setNumber} of ${exerciseName}${
                isDropSet ? ' (drop set)' : ''
              }`}
              title={set.status === 'pending' ? 'Mark this set done' : 'Reopen this set'}
              className="inline-flex items-center gap-1"
            >
              <StatusTag status={set.isPr ? 'pr' : set.status} />
            </button>
            <button
              type="button"
              aria-label={`Delete set ${setNumber} of ${exerciseName}`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              className="text-[11px] text-ink-muted opacity-0 transition-opacity duration-150 hover:text-danger focus-visible:opacity-100 group-hover:opacity-70"
            >
              ✕
            </button>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            data-testid={`slot-log-${rowId}-${setNumber}`}
            disabled={busy}
            onClick={() => onLog(prescribedDraft(prescription))}
            className="flex min-h-[36px] w-full flex-col items-start justify-center gap-0.5 border border-dashed border-hairline px-2 py-1 text-left transition-colors duration-150 hover:border-accent hover:text-accent disabled:opacity-40"
          >
            <span className="l2f-tabular font-mono text-[12px] font-bold leading-none text-ink-muted">
              {targetLabel(prescription, unit)}
            </span>
            <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.08em] text-ink-muted">
              <PlusIcon className="text-[10px]" /> Log set {setNumber}
            </span>
          </button>
          <div className="flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={onStartEdit}
              className="text-[9px] font-bold uppercase tracking-[0.06em] text-ink-muted hover:text-accent"
            >
              Custom
            </button>
            {canRemoveSlot ? (
              <button
                type="button"
                aria-label={`Remove set ${setNumber} of ${exerciseName}`}
                onClick={onRemoveSlot}
                className="text-[11px] text-ink-muted hover:text-danger"
              >
                ✕
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
